import React, { useState, useEffect } from 'react';
import {
  Container,
  Heading,
  Button,
  useDisclosure,
  Fade,
  Text,
  Box,
  VStack,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { FaMagic, FaGamepad } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import ActiveGames from './ActiveGames';
import CreateGame from './CreateGame';

function Magic() {
  const { userData } = useAuth();
  const [activeGames, setActiveGames] = useState([]);
  const [finishLoading, setFinishLoading] = useState(false);

  const socio = userData && userData.tesseramenti[0];

  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  const fetchActiveGames = async () => {
    try {
      const response = await axios.get('/api/games/active');
      setActiveGames(response.data.games);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching active games:', error);
    }
  };

  const handleFinishGame = async (gameId) => {
    setFinishLoading(true);
    try {
      await axios.post(
        '/api/games/finish',
        { gameId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        }
      );

      fetchActiveGames();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error finishing game:', error);
    } finally {
      setFinishLoading(false);
    }
  };

  const onGameCreated = () => {
    onDrawerClose();
    fetchActiveGames();
  };

  useEffect(() => {
    document.body.classList.add('magic');

    fetchActiveGames();

    const interval = setTimeout(() => {
      fetchActiveGames();
    }, 30000);

    return () => {
      document.body.classList.remove('magic');
      clearTimeout(interval);
    };
  }, []);

  return (
    <Box bg="#36393f" minH="100vh" overflowX="hidden" color="white">
      {/* HEADER SECTION - Discord Style */}
      <Box bg="#2f3136" py={16}>
        <Container maxW="7xl">
          <VStack spacing={8} textAlign="center">
            <Badge
              bg="#ffcc00"
              color="black"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              fontWeight="600"
            >
              🎮 Magic Games
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="800"
              color="white"
              letterSpacing="-0.02em"
            >
              Partite Magic Attive
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="gray.300"
              maxW="2xl"
              lineHeight="1.7"
            >
              Crea una nuova partita o unisciti a quelle già in corso.
              <Text as="span" color="#ffcc00" fontWeight="600">
                Divertiti con Magic: The Gathering!
              </Text>
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* MAIN CONTENT SECTION */}
      <Container maxW="7xl" py={16}>
        <VStack spacing={12}>
          {/* Create Game Button */}
          <Box w="100%" textAlign="center">
            <Button
              onClick={onDrawerOpen}
              bg="#ffcc00"
              color="black"
              size="xl"
              px={10}
              py={6}
              borderRadius="12px"
              fontWeight="700"
              fontSize="lg"
              rightIcon={<MdAdd />}
              _hover={{
                bg: '#e6b800',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.4)',
              }}
              transition="all 0.3s ease"
            >
              Crea Nuova Partita
            </Button>
          </Box>

          {/* Active Games Section */}
          <Box w="100%">
            <VStack spacing={6} align="stretch">
              <VStack spacing={4} textAlign="center">
                <Badge
                  bg="#ffcc00"
                  color="black"
                  px={4}
                  py={2}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="600"
                >
                  <Icon as={FaGamepad} mr={2} />
                  Partite Attive
                </Badge>
                <Heading as="h2" size="lg" color="white" fontWeight="700">
                  {activeGames.length > 0
                    ? `${activeGames.length} Partita${activeGames.length > 1 ? 'e' : ''} in Corso`
                    : 'Nessuna Partita Attiva'}
                </Heading>
              </VStack>

              <Fade in={Boolean(activeGames.length)}>
                {activeGames.length ? (
                  <ActiveGames
                    games={activeGames}
                    handleFinishGame={handleFinishGame}
                    fetchActiveGames={fetchActiveGames}
                    finishLoading={finishLoading}
                    socio={socio}
                  />
                ) : (
                  <Box
                    bg="#2f3136"
                    borderRadius="20px"
                    p={12}
                    textAlign="center"
                    border="1px solid"
                    borderColor="gray.600"
                  >
                    <Icon as={FaMagic} boxSize={16} color="#ffcc00" mb={6} />
                    <Text fontSize="xl" color="gray.300" mb={4}>
                      Nessuna partita attiva al momento
                    </Text>
                    <Text color="gray.400" fontSize="md">
                      Crea la tua prima partita per iniziare a giocare!
                    </Text>
                  </Box>
                )}
              </Fade>
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Create Game Modal */}
      {isDrawerOpen && (
        <CreateGame
          onDrawerClose={onDrawerClose}
          onGameCreated={onGameCreated}
        />
      )}
    </Box>
  );
}

export default Magic;
